import { Link } from 'gatsby';
import styled, { css } from 'styled-components';

/* Ink breakpoints for media queries (max-width) */
export const MOBILE_BREAKPOINT = '599px';
export const TABLET_PORTRAIT_BREAKPOINT = '899px';

const spacingCustomProperties = css`
  --space-0: 0;
  --space-1: 4px;
  --space-2: 8px;
  --space-3: 12px;
  --space-4: 16px;
  --space-5: 24px;
  --space-6: 32px;
  --space-7: 48px;
  --space-8: 64px;
  --space-9: 96px;
  --space-10: 128px;
  --space-11: 192px;
`;

const colorCustomProperties = css`
  --black: #1a1a1a;
  --white: #ffffff;
  --blue-70: #285da3;
  --blue-40: #8babd6;
  --gray-100: #2c3030;
  --gray-90: #394040;
  --gray-80: #656b6b;
  --gray-70: #9c9f9f;
  --gray-50: #cecfcf;
  --brown-10: #fbfaf9;
  --orange-50: #ff7d55;
`;

export const DarkPageWrapper = styled.div`
  ${colorCustomProperties}
  ${spacingCustomProperties}

  --border-color: var(--gray-90);
  --highlight-color: var(--orange-50);

  --content-container-inset: var(--space-8);
  --content-min-margin: var(--space-7);
  --content-max-width: 1400px;
  --content-padding: max(var(--content-min-margin), calc((100% - var(--content-max-width)) / 2));

  --section-gap: var(--space-6);
  --block-padding: var(--space-9);
  --header-spacing: var(--space-7);

  --font-size-heading-1-display: 124px;
  --font-size-heading-1: 48px;
  --font-size-heading-2: 32px;
  --font-size-heading-3: 24px;
  --font-size-body-1-display: 24px;
  --font-size-body-1: 20px;

  --line-height-heading-1: 1.1;
  --cta-margin: var(--space-6);

  background-color: var(--black);
  color: var(--white);
  display: flex;
  flex-direction: column;
  font-family: var(--text-sans);
  min-height: 100vh;

  @media (max-width: ${TABLET_PORTRAIT_BREAKPOINT}) {
    --content-container-inset: var(--space-6);
    --content-padding: 0;
    --section-gap: var(--space-4);
    --block-padding: var(--space-7);
    --header-spacing: var(--space-5);

    --font-size-heading-1-display: 64px;
    --font-size-heading-1: 32px;
    --font-size-heading-2: 24px;
    --font-size-heading-3: 20px;
    --font-size-body-1-display: 20px;
    --font-size-body-1: 18px;

    --line-height-heading-1: 1.3;
    --cta-margin: var(--space-6);
  }
`;

export const sharedContentBlockRules = css`
  border-inline: 1px solid transparent;
  max-width: var(--content-max-width);
  margin-inline: var(--content-padding);

  @media (max-width: ${TABLET_PORTRAIT_BREAKPOINT}) {
    border-inline: none;
  }
`;

export const SearchContainer = styled.div`
  min-width: 240px;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    min-width: unset;

    svg,
    svg path {
      fill: #ffffff;
    }
  }

  & #ink-search-input {
    background-color: var(--black);
    border-color: var(--gray-50) !important;
    color: var(--white);
  }
  & #ink-search-input:hover {
    border-color: var(--white) !important;
  }
  & #ink-search-input::placeholder {
    color: var(--gray-70);
  }
  & #ink-search-input:focus {
    border-color: var(--blue-70);
  }
  & #ink-search-input:focus-visible {
    box-shadow: 0 0 0 4px var(--blue-40);
  }
`;

export const Search = styled.input.attrs({ type: 'search' })`
  border: 1px solid var(--white);
  background-color: var(--black);
  color: var(--white);
  padding: var(--space-3) var(--space-4);
  border-radius: 4px;
  font-size: inherit;
  font-family: inherit;
  transition: background-color 150ms ease-out;
  width: 240px;

  &::placeholder {
    color: var(--white);
    opacity: 0.6;
  }

  &:focus {
    outline: none;
    background-color: var(--white);
    color: var(--black);

    &::placeholder {
      color: var(--black);
      opacity: 0.6;
    }
  }
`;

const variantStyles = {
  'heading-1-display': {
    element: 'h1',
    fontSize: 'var(--font-size-heading-1-display)',
    fontFamily: 'var(--text-serif)',
    lineHeight: '1.1',
    fontWeight: 'normal',
  },
  'heading-1': {
    element: 'h1',
    fontSize: 'var(--font-size-heading-1)',
    fontFamily: 'var(--text-serif)',
    lineHeight: 'var(--line-height-heading-1)',
    fontWeight: 'normal',
  },
  'heading-2': {
    element: 'h2',
    fontSize: 'var(--font-size-heading-2)',
    fontFamily: 'var(--text-serif)',
    lineHeight: '1.1',
    fontWeight: 'normal',
  },
  'heading-3': {
    element: 'h3',
    fontSize: 'var(--font-size-heading-3)',
    fontFamily: 'var(--text-serif)',
    lineHeight: '1.2',
    fontWeight: 'normal',
  },
  'heading-4': {
    element: 'h4',
    fontSize: '16px',
    fontFamily: 'var(--text-sans)',
    lineHeight: '1.3',
    fontWeight: '600',
  },
  'body-1-display': {
    element: 'p',
    fontSize: 'var(--font-size-body-1-display)',
    fontFamily: 'var(--text-sans)',
    lineHeight: '1.4',
    fontWeight: 'normal',
  },
  'body-1': {
    element: 'p',
    fontSize: 'var(--font-size-body-1)',
    fontFamily: 'var(--text-sans)',
    lineHeight: '1.4',
    fontWeight: 'normal',
  },
  'body-2': {
    element: 'p',
    fontSize: '16px',
    fontFamily: 'var(--text-sans)',
    lineHeight: '1.5',
    fontWeight: 'normal',
  },
} as const;

type DarkModeTextVariant = keyof typeof variantStyles;

export const DarkModeText = styled.p.attrs<{ variant: DarkModeTextVariant; as?: string }>(({ variant, as }) => ({
  as: as || variantStyles[variant].element,
}))<{ variant: DarkModeTextVariant; balance?: boolean; as?: string }>`
  ${({ variant, balance }) => {
    const styles = variantStyles[variant];
    return css`
      font-family: ${styles.fontFamily};
      font-size: ${styles.fontSize};
      line-height: ${styles.lineHeight};
      font-weight: ${styles.fontWeight};
      max-width: ${variant === 'body-1' ? '80ch' : 'none'};
      ${balance && 'text-wrap: balance;'}
    `;
  }}
`;

export const HighlightedText = styled.span`
  color: var(--highlight-color);
`;

export const DarkModeButton = styled(Link)`
  border: 1px solid var(--white);
  background-color: var(--black);
  color: var(--white);
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-4);
  border-radius: 4px;
  transition: background-color 150ms ease-out;
  cursor: pointer;

  &:hover {
    background-color: var(--white);
    color: var(--black);
    text-decoration: none;
  }
`;
